<template>
  <!--
    StepCustomVariables.vue - Step 5: Generel Information
    =====================================================
    Viser dynamiske custom variables defineret i backend.

    Felttyper:
    - string: Tekstfelt
    - number: Tal-input
    - date: Datovælger
    - time: Tidsvælger
    - boolean: Ja/Nej radio-knapper
    - select: Dropdown
    - multiline string: Tekstområde
  -->
  <div class="step-custom-variables">
    <!-- Overskrift -->
    <div class="step-header mb-2">
      <h4>{{ $t('DEBTIA_CREATE_CONFIRM_GENERAL_INFO') }}</h4>
      <p class="text-muted mb-0">
        {{ $t('DEBTIA_CREATE_VARS_SUBTITLE') }}
      </p>
    </div>

    <b-card>
      <b-row>
        <b-col
          v-for="variable in visibleVariables"
          :key="variable.id"
          cols="12"
          md="6"
        >
          <b-form-group
            :label="getVariableLabel(variable)"
            :label-for="`var-${variable.id}`"
          >
            <!-- TIME input -->
            <b-form-timepicker
              v-if="variable.type === 'time'"
              :id="`var-${variable.id}`"
              :value="getVariableValue(variable)"
              locale="da"
              @input="updateValue(variable.variable, $event)"
            />

            <!-- DATE input -->
            <b-form-datepicker
              v-else-if="variable.type === 'date'"
              :id="`var-${variable.id}`"
              :value="getVariableValue(variable)"
              locale="da"
              @input="updateValue(variable.variable, $event)"
            />

            <!-- MULTILINE STRING (textarea) -->
            <b-form-textarea
              v-else-if="variable.type === 'multiline string'"
              :id="`var-${variable.id}`"
              :value="getVariableValue(variable)"
              rows="3"
              @input="updateValue(variable.variable, $event)"
            />

            <!-- SELECT (dropdown) -->
            <v-select
              v-else-if="variable.type === 'select'"
              :id="`var-${variable.id}`"
              :value="getVariableValue(variable)"
              :options="getSelectOptions(variable)"
              @input="updateValue(variable.variable, $event)"
            />

            <!-- BOOLEAN (radio buttons) -->
            <b-form-radio-group
              v-else-if="variable.type === 'boolean'"
              :id="`var-${variable.id}`"
              :checked="getVariableValue(variable)"
              :options="booleanOptions"
              @change="updateValue(variable.variable, $event)"
            />

            <!-- NUMBER input -->
            <b-form-input
              v-else-if="variable.type === 'number'"
              :id="`var-${variable.id}`"
              :value="getVariableValue(variable)"
              type="number"
              @input="updateValue(variable.variable, $event)"
            />

            <!-- DEFAULT: STRING input -->
            <b-form-input
              v-else
              :id="`var-${variable.id}`"
              :value="getVariableValue(variable)"
              @input="updateValue(variable.variable, $event)"
            />

            <!-- Required field error -->
            <small
              v-if="variable.is_required && !getVariableValue(variable)"
              class="text-danger"
            >
              {{ $t('DEBTIA_CREATE_VARS_REQUIRED') }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Ingen variabler -->
    <b-alert
      v-if="visibleVariables.length === 0"
      variant="info"
      show
    >
      <div class="alert-body">
        <feather-icon
          icon="InfoIcon"
          class="mr-50"
        />
        {{ $t('DEBTIA_CREATE_VARS_NO_VARIABLES') }}
      </div>
    </b-alert>
  </div>
</template>

<script>
/**
 * StepCustomVariables Script
 *
 * Dynamisk formular baseret på variable-definitioner fra backend.
 */
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormDatepicker,
  BFormTimepicker,
  BFormRadioGroup,
  BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { computed } from '@vue/composition-api'
import i18n from '@/libs/i18n'

export default {
  name: 'StepCustomVariables',

  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormDatepicker,
    BFormTimepicker,
    BFormRadioGroup,
    BAlert,
    vSelect,
  },

  props: {
    definitions: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
  },

  setup(props, { emit }) {
    // Boolean options for radio buttons
    const booleanOptions = computed(() => [
      { text: i18n.t('DEBTIA_CREATE_VARS_YES'), value: 'true' },
      { text: i18n.t('DEBTIA_CREATE_VARS_NO'), value: 'false' },
    ])

    /**
     * Filtrerer variabler der skal vises på oprettelsessiden.
     */
    const visibleVariables = computed(() => props.definitions.filter(v => v.display_in_add_edit_page === 1
        || v.display_in_add_edit_page === true))

    /**
     * Henter label for en variabel.
     * Forsøger at oversætte, ellers bruger variabel-navnet.
     */
    function getVariableLabel(variable) {
      // Her kunne man tilføje i18n oversættelse: $t(variable.variable)
      // For nu returnerer vi bare variabel-navnet med evt. påkrævet-markering
      const label = variable.variable.replace(/_/g, ' ')
      return variable.is_required ? `${label} *` : label
    }

    /**
     * Henter den aktuelle værdi for en variabel.
     */
    function getVariableValue(variable) {
      const found = props.values.find(v => v.customVarLabel === variable.variable)
      return found ? found.value : ''
    }

    /**
     * Henter dropdown-options for en select-variabel.
     */
    function getSelectOptions(variable) {
      if (variable.options && Array.isArray(variable.options)) {
        return variable.options
      }
      return []
    }

    /**
     * Opdaterer værdien for en variabel.
     */
    function updateValue(variableName, value) {
      emit('update-value', variableName, value)
    }

    return {
      booleanOptions,
      visibleVariables,
      getVariableLabel,
      getVariableValue,
      getSelectOptions,
      updateValue,
    }
  },
}
</script>

<style lang="scss" scoped>
/* Custom variables styles */
</style>
